/**
 * Custom fabric material + color swatches (cart line item properties, optional multi-sample).
 * Scoped per block instance; expects JSON config in #cffco-config-{blockId}.
 */
function formatCountTemplate(template, current, max) {
  if (!template) return `${current}/${max} free samples selected`;
  return template.replace(/\{\{\s*current\s*\}\}/gi, String(current)).replace(/\{\{\s*max\s*\}\}/gi, String(max));
}

/**
 * Reads ?colour=&fabric= from the current URL (relayed here by a "Shop by
 * colour" swatch click via sections/mh-colours.liquid + the link-rewrite in
 * assets/newcustom.js). Used below to pre-select the matching material
 * button + colour swatch on load instead of defaulting to the first of each.
 */
function getSwatchContextFromQuery() {
  try {
    var params = new URLSearchParams(window.location.search);
    return { fabric: params.get('fabric'), colour: params.get('colour') };
  } catch (e) {
    return { fabric: null, colour: null };
  }
}

function normalizeSwatchValue(value) {
  return (value || '').trim().toLowerCase();
}

function initCustomFabricColorOptions(config) {
  const root = document.querySelector(
    '[data-custom-fabric-color-options-root][data-block-id="' + String(config.blockId) + '"]'
  );
  if (!root) return;

  const productForm =
    document.getElementById(config.formId) || document.querySelector('form[data-type="add-to-cart-form"]');

  const materialButtonsContainer = root.querySelector('[data-material-buttons]');
  const materialButtons = materialButtonsContainer
    ? materialButtonsContainer.querySelectorAll('[data-material-button]')
    : [];
  const colorGroups = root.querySelectorAll('[data-color-group]');
  const selectedColorSpans = root.querySelectorAll('[data-selected-color-value]');
  const selectedMaterialSpans = root.querySelectorAll('[data-selected-material-value]');
  const punchlineEl = root.querySelector('[data-material-punchline-value]');
  const punchlineTextEl = punchlineEl ? punchlineEl.querySelector('[data-material-punchline-text]') : null;

  function setSelectedColorText(val) {
    selectedColorSpans.forEach((el) => {
      el.textContent = val;
    });
  }

  function setMaterialPunchline(text) {
    if (!punchlineEl) return;
    if (punchlineTextEl) punchlineTextEl.textContent = text || '';
    punchlineEl.hidden = !text;
  }

  const enableMultipleSelection = Boolean(config.enableMultipleSelection);
  const productId = config.productId;
  const countTemplate = config.countTemplate || '';

  const multiSelector = enableMultipleSelection
    ? Array.from(document.querySelectorAll('[data-multi-selector]')).find(
        (el) => el.getAttribute('data-product-id') === String(productId)
      )
    : null;

  const MAX_SAMPLES = 5;
  let selectedMaterialColorPairs = new Map();
  let variantData = {};
  let cartFreeItemsCount = 0;
  let isLimitReached = false;

  // Hidden inputs container for additional items (samples 2-5)
  const hiddenInputsContainer = enableMultipleSelection
    ? document.getElementById(`multiple-variant-hidden-inputs-${config.blockId}`)
    : null;

  if (enableMultipleSelection) {
    const variantJsonEl = document.getElementById(`cffco-variant-json-${config.blockId}`);
    if (variantJsonEl) {
      try {
        const variants = JSON.parse(variantJsonEl.textContent);
        variants.forEach((variant) => {
          variantData[variant.id] = variant;
        });
      } catch (_) {}
    }

    // In multi-select mode no swatch should start visually selected — the Liquid
    // template pre-selects the first one for the single-select flow only.
    root.querySelectorAll('.custom-fabric-color-options__swatch--selected').forEach((el) => {
      el.classList.remove('custom-fabric-color-options__swatch--selected');
    });

    // Move hidden inputs container into the product form so they're included in FormData
    if (productForm && hiddenInputsContainer) {
      productForm.appendChild(hiddenInputsContainer);
    }
  }

  const materialWrapperId = `fabric-material-hidden-wrapper-${config.blockId}`;
  let hiddenWrapper = document.getElementById(materialWrapperId);
  if (!hiddenWrapper && productForm) {
    hiddenWrapper = document.createElement('div');
    hiddenWrapper.id = materialWrapperId;
    productForm.appendChild(hiddenWrapper);
  }

  let materialInput = null;
  let colorInput = null;

  function findHiddenByPropertyName(wrapper, label) {
    const suffix = `properties[${label}]`;
    return Array.from(wrapper.querySelectorAll('input[type="hidden"]')).find((input) => input.name === suffix);
  }

  if (hiddenWrapper) {
    materialInput = findHiddenByPropertyName(hiddenWrapper, config.cartLabel);
    if (!materialInput) {
      materialInput = document.createElement('input');
      materialInput.type = 'hidden';
      materialInput.name = `properties[${config.cartLabel}]`;
      hiddenWrapper.appendChild(materialInput);
    }

    colorInput = findHiddenByPropertyName(hiddenWrapper, config.optionLabel);
    if (!colorInput) {
      colorInput = document.createElement('input');
      colorInput.type = 'hidden';
      colorInput.name = `properties[${config.optionLabel}]`;
      hiddenWrapper.appendChild(colorInput);
    }
  }

  function showGroup(bundleIndex) {
    colorGroups.forEach((group) => {
      const groupBundleIndex = group.getAttribute('data-bundle-index');
      if (groupBundleIndex === String(bundleIndex)) {
        group.classList.remove('hidden');
      } else {
        group.classList.add('hidden');
      }
    });
  }

  function clearSelectedInGroup(bundleIndex) {
    const group = root.querySelector(`[data-color-group][data-bundle-index="${bundleIndex}"]`);
    if (!group) return;
    group.querySelectorAll('[data-custom-color-swatch]').forEach((btn) => {
      btn.classList.remove('custom-fabric-color-options__swatch--selected');
    });
  }

  function selectFirstColorInGroup(bundleIndex) {
    const group = root.querySelector(`[data-color-group][data-bundle-index="${bundleIndex}"]`);
    if (!group) return;
    const first = group.querySelector('[data-custom-color-swatch]');
    if (first) {
      first.classList.add('custom-fabric-color-options__swatch--selected');
      const val = first.getAttribute('data-custom-option-value');
      setSelectedColorText(val);
      if (colorInput) colorInput.value = val;
    }
  }

  // Tells "Select sofa type" (snippets/sofa-type-selector.liquid, via
  // assets/newcustom.js) what's currently picked here, so its sibling links can
  // carry the *live* fabric + colour forward as ?fabric=&colour= — not just
  // whatever arrived in this page's own URL. Single-select mode only; the
  // multi-sample flow has no fixed "current selection" to forward.
  function notifySelectionChange() {
    if (enableMultipleSelection) return;
    const materialEl = root.querySelector('[data-selected-material-value]');
    const colourEl = root.querySelector('[data-selected-color-value]');
    document.dispatchEvent(
      new CustomEvent('cffco:selection', {
        detail: {
          fabric: materialEl ? materialEl.textContent.trim() : '',
          colour: colourEl ? colourEl.textContent.trim() : '',
        },
      })
    );
  }

  function getSelectedMaterial() {
    const selectedMaterialButton = root.querySelector(
      '[data-material-button].custom-fabric-color-options__material-button--selected'
    );
    if (selectedMaterialButton) {
      return selectedMaterialButton.getAttribute('data-material-value');
    }
    return null;
  }

  function updateMaterialSelection(button) {
    const bundleIndex = button.getAttribute('data-bundle-index');
    const materialName = button.getAttribute('data-material-value');
    materialButtons.forEach((btn) => {
      btn.classList.remove('custom-fabric-color-options__material-button--selected');
      btn.setAttribute('aria-pressed', 'false');
    });
    button.classList.add('custom-fabric-color-options__material-button--selected');
    button.setAttribute('aria-pressed', 'true');
    showGroup(bundleIndex);

    if (enableMultipleSelection) {
      // In multi-select mode: never auto-select a colour when switching material.
      // The user picks each colour intentionally; auto-selecting would silently
      // add it to the selection and confuse the count display.
    } else {
      // Single-select mode: keep the existing selection or fall back to the first colour.
      const targetGroup = root.querySelector(`[data-color-group][data-bundle-index="${bundleIndex}"]`);
      const existingSelected = targetGroup
        ? targetGroup.querySelector('[data-custom-color-swatch].custom-fabric-color-options__swatch--selected')
        : null;

      if (existingSelected) {
        const colorValue = existingSelected.getAttribute('data-custom-option-value');
        setSelectedColorText(colorValue);
        if (colorInput) colorInput.value = colorValue;
      } else {
        clearSelectedInGroup(bundleIndex);
        selectFirstColorInGroup(bundleIndex);
      }
    }

    selectedMaterialSpans.forEach((span) => {
      span.textContent = materialName;
    });
    if (materialInput) materialInput.value = materialName;
    setMaterialPunchline(button.getAttribute('data-material-punchline'));
    notifySelectionChange();
  }

  function checkCartContents() {
    return fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        cartFreeItemsCount = cart.items.filter((item) => item.price === 0).length;
        isLimitReached = cartFreeItemsCount >= MAX_SAMPLES;
        updateCartStatus();
        if (isLimitReached) {
          disableSelector();
        } else {
          enableSelector();
        }
        return cartFreeItemsCount;
      })
      .catch(() => 0);
  }

  function getDynamicMaxSamples() {
    return Math.max(0, MAX_SAMPLES - cartFreeItemsCount);
  }

  function updateCartStatus() {
    if (!multiSelector) return;
    const cartStatusEl = multiSelector.querySelector('[data-cart-status]');
    const cartStatusMessageEl = multiSelector.querySelector('[data-cart-status-message]');
    if (!cartStatusEl || !cartStatusMessageEl) return;

    if (cartFreeItemsCount > 0) {
      cartStatusEl.hidden = false;
      const remaining = getDynamicMaxSamples();
      if (remaining === 0) {
        cartStatusMessageEl.textContent = `You have ${cartFreeItemsCount} free samples in your cart. Remove some to add more.`;
      } else {
        cartStatusMessageEl.textContent = `You have ${cartFreeItemsCount} free samples in cart. You can add ${remaining} more.`;
      }
    } else {
      cartStatusEl.hidden = true;
    }
  }

  function disableSelector() {
    if (!multiSelector) return;
    multiSelector.classList.add('disabled');
    const addToCartBtn = multiSelector.querySelector('[data-add-multiple-to-cart]');
    const selectAllBtn = multiSelector.querySelector('[data-select-all]');
    const clearAllBtn = multiSelector.querySelector('[data-clear-all]');
    if (addToCartBtn) addToCartBtn.disabled = true;
    if (selectAllBtn) selectAllBtn.disabled = true;
    if (clearAllBtn) clearAllBtn.disabled = true;
    selectedMaterialColorPairs.clear();
    updateSelectedCount();
    updateSwatchStyles();
  }

  function enableSelector() {
    if (!multiSelector) return;
    multiSelector.classList.remove('disabled');
    const addToCartBtn = multiSelector.querySelector('[data-add-multiple-to-cart]');
    const selectAllBtn = multiSelector.querySelector('[data-select-all]');
    const clearAllBtn = multiSelector.querySelector('[data-clear-all]');
    if (addToCartBtn) addToCartBtn.disabled = false;
    if (selectAllBtn) selectAllBtn.disabled = false;
    if (clearAllBtn) clearAllBtn.disabled = false;
  }

  function updateSwatchAvailability() {
    const dynamicMax = getDynamicMaxSamples();
    const isAtLimit = selectedMaterialColorPairs.size >= dynamicMax || isLimitReached;
    root.querySelectorAll('[data-custom-color-swatch][data-option-value]').forEach((swatch) => {
      const optionValue = swatch.getAttribute('data-option-value');
      if (optionValue && !selectedMaterialColorPairs.has(optionValue)) {
        if (isAtLimit) {
          swatch.style.opacity = '0.5';
          swatch.style.cursor = 'not-allowed';
          swatch.setAttribute(
            'title',
            `Maximum ${dynamicMax} samples already selected or in cart`
          );
        } else {
          swatch.style.opacity = '';
          swatch.style.cursor = '';
          swatch.removeAttribute('title');
        }
      }
    });
  }

  function updateSelectedCount() {
    if (!multiSelector) return;
    const selectedCountEl = multiSelector.querySelector('[data-selected-count]');
    const selectedItemsEl = multiSelector.querySelector('[data-selected-items]');
    const selectedItemsWrap = multiSelector.querySelector('[data-selected-items-wrap]');
    const addToCartBtn = multiSelector.querySelector('[data-add-multiple-to-cart]');
    const selectAllBtn = multiSelector.querySelector('[data-select-all]');

    const count = selectedMaterialColorPairs.size;
    const dynamicMax = getDynamicMaxSamples();
    if (selectedCountEl) {
      selectedCountEl.textContent = formatCountTemplate(countTemplate, count, dynamicMax);
    }
    if (addToCartBtn) addToCartBtn.disabled = count === 0 || isLimitReached;

    if (selectAllBtn) {
      if (count >= dynamicMax || isLimitReached) {
        selectAllBtn.disabled = true;
        selectAllBtn.textContent = 'Limit reached';
      } else {
        selectAllBtn.disabled = false;
        selectAllBtn.textContent = `Select ${dynamicMax} random`;
      }
    }

    if (selectedItemsEl) {
      selectedItemsEl.innerHTML = '';
      let colorIndex = 1;
      selectedMaterialColorPairs.forEach((material, optionValue) => {
        const tag = document.createElement('span');
        tag.className = 'custom-fabric-color-options__tag';
        tag.innerHTML = `Sample ${colorIndex}: ${optionValue} (${material}) <button type="button" data-remove-option="${encodeURIComponent(optionValue)}">&times;</button>`;
        const removeBtn = tag.querySelector('[data-remove-option]');
        removeBtn.addEventListener('click', () => {
          selectedMaterialColorPairs.delete(decodeURIComponent(removeBtn.getAttribute('data-remove-option')));
          updateSwatchStyles();
          updateSelectedCount();
        });
        selectedItemsEl.appendChild(tag);
        colorIndex++;
      });
    }
    if (selectedItemsWrap) {
      selectedItemsWrap.hidden = count === 0;
    }
    updateSwatchAvailability();
  }

  function updateSwatchStyles() {
    root.querySelectorAll('.custom-fabric-color-options__swatch-item').forEach((el) => {
      el.classList.remove('multiple-selected');
    });
    selectedMaterialColorPairs.forEach((material, optionValue) => {
      root.querySelectorAll('[data-option-value]').forEach((swatchBtn) => {
        if (swatchBtn.getAttribute('data-option-value') === optionValue) {
          const container = swatchBtn.closest('.custom-fabric-color-options__swatch-item');
          if (container) container.classList.add('multiple-selected');
        }
      });
    });
  }

  function showLimitMessage() {
    if (!multiSelector) return;
    const existingMsg = multiSelector.querySelector('.limit-message');
    if (existingMsg) return;
    const limitMsg = document.createElement('div');
    limitMsg.className = 'limit-message paragraph';
    limitMsg.style.cssText =
      'color: rgb(var(--color-foreground-rgb)); margin: var(--padding-xs) 0; text-align: center; font-weight: 600;';
    limitMsg.textContent =
      'Maximum 5 samples allowed. Remove some from your cart to add more.';
    const anchor = multiSelector.querySelector('[data-selected-count]');
    if (anchor && anchor.parentElement) {
      anchor.parentElement.after(limitMsg);
      setTimeout(() => limitMsg.remove(), 4000);
    }
  }

  /**
   * Injects all multi-sample selections as hidden form inputs BEFORE the native
   * submit button is clicked. This is critical: product-form-component reads
   * `new FormData(form)` synchronously at the start of its submit handler, so
   * inputs must already be in the DOM at that point.
   *
   * First selection → goes as standard properties[] on the main line item.
   * Selections 2-5 → go as items[n][] (Shopify multi-add FormData format).
   */
  function prepareFormInputs() {
    if (!hiddenInputsContainer || !hiddenWrapper) return;

    // Clear any previously built inputs
    hiddenInputsContainer.innerHTML = '';
    if (materialInput) { materialInput.remove(); materialInput = null; }
    if (colorInput) { colorInput.remove(); colorInput = null; }

    const firstVariant = Object.values(variantData)[0];
    if (!firstVariant) return;

    let itemIndex = 0;
    let isFirstItem = true;

    selectedMaterialColorPairs.forEach((material, optionValue) => {
      if (isFirstItem) {
        // Main form item: properties[] submitted alongside the existing id/quantity inputs
        const m = document.createElement('input');
        m.type = 'hidden';
        m.name = `properties[${config.cartLabel}]`;
        m.value = material || 'Unknown Material';
        hiddenWrapper.appendChild(m);

        const c = document.createElement('input');
        c.type = 'hidden';
        c.name = `properties[${config.optionLabel}]`;
        c.value = optionValue;
        hiddenWrapper.appendChild(c);

        isFirstItem = false;
      } else {
        // Additional items: Shopify multi-add format (items[n][id], items[n][quantity], …)
        const inputId = document.createElement('input');
        inputId.type = 'hidden';
        inputId.name = `items[${itemIndex}][id]`;
        inputId.value = String(firstVariant.id);

        const inputQty = document.createElement('input');
        inputQty.type = 'hidden';
        inputQty.name = `items[${itemIndex}][quantity]`;
        inputQty.value = '1';

        const mInput = document.createElement('input');
        mInput.type = 'hidden';
        mInput.name = `items[${itemIndex}][properties][${config.cartLabel}]`;
        mInput.value = material || 'Unknown Material';

        const cInput = document.createElement('input');
        cInput.type = 'hidden';
        cInput.name = `items[${itemIndex}][properties][${config.optionLabel}]`;
        cInput.value = optionValue;

        hiddenInputsContainer.appendChild(inputId);
        hiddenInputsContainer.appendChild(inputQty);
        hiddenInputsContainer.appendChild(mInput);
        hiddenInputsContainer.appendChild(cInput);

        itemIndex += 1;
      }
    });
  }

  if (enableMultipleSelection && multiSelector) {
    const addToCartBtn = multiSelector.querySelector('[data-add-multiple-to-cart]');
    const selectAllBtn = multiSelector.querySelector('[data-select-all]');
    const clearAllBtn = multiSelector.querySelector('[data-clear-all]');

    selectAllBtn?.addEventListener('click', () => {
      if (isLimitReached) return;
      const dynamicMax = getDynamicMaxSamples();
      selectedMaterialColorPairs.clear();
      const activeGroup = root.querySelector('[data-color-group]:not(.hidden)');
      if (activeGroup) {
        const availableSwatches = activeGroup.querySelectorAll('[data-custom-color-swatch]');
        const shuffled = Array.from(availableSwatches).sort(() => 0.5 - Math.random());
        const samplesToSelect = shuffled.slice(0, dynamicMax);
        const currentMaterial = getSelectedMaterial();
        samplesToSelect.forEach((swatch) => {
          const optionValue = swatch.getAttribute('data-option-value');
          if (optionValue && currentMaterial) {
            selectedMaterialColorPairs.set(optionValue, currentMaterial);
          }
        });
      }
      updateSwatchStyles();
      updateSelectedCount();
    });

    clearAllBtn?.addEventListener('click', () => {
      selectedMaterialColorPairs.clear();
      updateSwatchStyles();
      updateSelectedCount();
    });

    addToCartBtn?.addEventListener('click', () => {
      if (selectedMaterialColorPairs.size === 0 || isLimitReached) return;
      if (!productForm) return;

      // ── KEY FIX ──────────────────────────────────────────────────────────────
      // Populate ALL hidden inputs NOW, synchronously, before nativeSubmitBtn.click().
      // product-form-component reads `new FormData(form)` at the very start of its
      // submit handler (product-form.js:376). If we waited until the form's submit
      // event, the component would already have a stale snapshot — meaning only the
      // first item's properties[] get sent and samples 2-5 are silently dropped.
      // ─────────────────────────────────────────────────────────────────────────
      prepareFormInputs();

      addToCartBtn.disabled = true;
      const prevLabel = addToCartBtn.textContent;
      addToCartBtn.textContent = 'Adding…';

      const nativeSubmitBtn = productForm.querySelector('button[type="submit"]');
      if (nativeSubmitBtn) {
        nativeSubmitBtn.click();
      } else {
        productForm.submit();
      }

      setTimeout(() => {
        const selectedCount = selectedMaterialColorPairs.size;
        selectedMaterialColorPairs.clear();
        updateSwatchStyles();
        checkCartContents().then(() => {
          updateSelectedCount();
          addToCartBtn.disabled = false;
          addToCartBtn.textContent = prevLabel;
          if (selectedCount > 0) {
            const successMsg = document.createElement('div');
            successMsg.className = 'paragraph';
            successMsg.style.cssText =
              'margin-top: var(--padding-sm); text-align: center;';
            successMsg.textContent = `${selectedCount} sample${selectedCount > 1 ? 's' : ''} added to cart.`;
            addToCartBtn.parentElement?.appendChild(successMsg);
            setTimeout(() => successMsg.remove(), 3000);
          }
        });
      }, 1500);
    });
  }

  // Colour/fabric arriving from a "Shop by colour" swatch (see getSwatchContextFromQuery
  // above) take priority over the plain first-material/first-colour default below, but
  // only for the single-select flow — multi-select (free fabric samples) always starts
  // with nothing selected on purpose (see the comment near the top of this function).
  const swatchContext = enableMultipleSelection
    ? { fabric: null, colour: null }
    : getSwatchContextFromQuery();

  if (materialButtons.length > 0) {
    let defaultButton =
      Array.prototype.find.call(materialButtons, (btn) =>
        btn.classList.contains('custom-fabric-color-options__material-button--selected')
      ) || materialButtons[0];

    if (swatchContext.fabric) {
      const matchedButton = Array.prototype.find.call(materialButtons, (btn) =>
        normalizeSwatchValue(btn.getAttribute('data-material-value')) === normalizeSwatchValue(swatchContext.fabric)
      );
      // If this product doesn't offer the requested fabric, fall back silently
      // to the plain default computed above.
      if (matchedButton) defaultButton = matchedButton;
    }

    updateMaterialSelection(defaultButton);
  } else {
    showGroup(0);
    if (!enableMultipleSelection) {
      selectFirstColorInGroup(0);
    }
  }

  // Colour match runs against whichever bundle ended up selected/visible above
  // (the matched fabric's bundle, or the plain default). If this product doesn't
  // offer the requested colour in that bundle, updateMaterialSelection above has
  // already left a sensible default (existing/first colour) selected — no error.
  if (!enableMultipleSelection && swatchContext.colour) {
    const activeGroup = root.querySelector('[data-color-group]:not(.hidden)');
    const matchedSwatch =
      activeGroup &&
      Array.prototype.find.call(activeGroup.querySelectorAll('[data-custom-color-swatch]'), (swatch) =>
        normalizeSwatchValue(swatch.getAttribute('data-custom-option-value')) === normalizeSwatchValue(swatchContext.colour)
      );

    if (matchedSwatch) {
      activeGroup.querySelectorAll('[data-custom-color-swatch]').forEach((btn) => {
        btn.classList.remove('custom-fabric-color-options__swatch--selected');
      });
      matchedSwatch.classList.add('custom-fabric-color-options__swatch--selected');
      const matchedValue = matchedSwatch.getAttribute('data-custom-option-value');
      setSelectedColorText(matchedValue);
      if (colorInput) colorInput.value = matchedValue;
    }
  }

  // Single-select only: ensure the color hidden input has an initial value
  if (!enableMultipleSelection && colorInput && !colorInput.value) {
    const firstGroup = root.querySelector('[data-color-group]');
    if (firstGroup) {
      const firstColor = firstGroup.querySelector('[data-custom-color-swatch]');
      if (firstColor) {
        colorInput.value = firstColor.getAttribute('data-custom-option-value') || '';
      }
    }
  }

  // Initial state is now fully resolved (default material/colour, or the one
  // matched from ?fabric=&colour=) — tell "Select sofa type" so its links are
  // correct even before the user touches this picker.
  notifySelectionChange();

  if (materialButtonsContainer) {
    materialButtonsContainer.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-material-button]');
      if (btn) {
        e.preventDefault();
        updateMaterialSelection(btn);
      }
    });
  }

  const swatchContainer = root.querySelector('[data-custom-color-option-buttons]');
  if (swatchContainer) {
    const handleSwatchSelection = (e) => {
      const swatch = e.target.closest('[data-custom-color-swatch]');
      if (!swatch) return;
      e.preventDefault();

      const group = swatch.closest('[data-color-group]');
      if (!group || group.classList.contains('hidden')) return;

      if (enableMultipleSelection) {
        const optionValue = swatch.getAttribute('data-option-value');
        if (!optionValue) return;
        e.stopPropagation();
        if (isLimitReached) {
          showLimitMessage();
          return;
        }
        if (selectedMaterialColorPairs.has(optionValue)) {
          selectedMaterialColorPairs.delete(optionValue);
        } else {
          const dynamicMax = getDynamicMaxSamples();
          if (selectedMaterialColorPairs.size < dynamicMax) {
            const currentMaterial = getSelectedMaterial();
            if (currentMaterial) {
              selectedMaterialColorPairs.set(optionValue, currentMaterial);
            }
          } else {
            showLimitMessage();
            return;
          }
        }
        updateSwatchStyles();
        updateSelectedCount();
      } else {
        group.querySelectorAll('[data-custom-color-swatch]').forEach((btn) => {
          btn.classList.remove('custom-fabric-color-options__swatch--selected');
        });
        swatch.classList.add('custom-fabric-color-options__swatch--selected');
        const colorValue = swatch.getAttribute('data-custom-option-value');
        setSelectedColorText(colorValue);
        if (colorInput) colorInput.value = colorValue;
        notifySelectionChange();
      }
    };
    swatchContainer.addEventListener('click', handleSwatchSelection);
    swatchContainer.addEventListener('touchend', handleSwatchSelection);
  }

  // Accordion layout only: mirror the selected swatch into the small preview
  // circle in the collapsed summary header (element only exists when the
  // "Layout style" block setting is "accordion" — see custom-fabric-color-options.liquid).
  // Purely additive: it observes the [data-selected-color-value] text nodes
  // that the code above already updates on every selection path, and no-ops
  // entirely when no preview element is present, so the default "expanded"
  // layout is completely unaffected.
  const previewSwatchEls = root.querySelectorAll('[data-selected-color-swatch]');
  if (previewSwatchEls.length) {
    const syncSwatchPreview = () => {
      const activeGroup = root.querySelector('[data-color-group]:not(.hidden)');
      const selectedSwatch =
        activeGroup &&
        activeGroup.querySelector('[data-custom-color-swatch].custom-fabric-color-options__swatch--selected');
      const inner =
        selectedSwatch &&
        selectedSwatch.querySelector(
          '.custom-fabric-color-options__swatch-color, .custom-fabric-color-options__swatch-img'
        );
      if (!inner) return;
      previewSwatchEls.forEach((el) => {
        el.innerHTML = '';
        el.appendChild(inner.cloneNode(true));
      });
    };
    syncSwatchPreview();
    root.querySelectorAll('[data-selected-color-value]').forEach((el) => {
      new MutationObserver(syncSwatchPreview).observe(el, { childList: true, characterData: true, subtree: true });
    });
  }

  if (enableMultipleSelection) {
    document.body.classList.add('multiple-variant-selector-active');
    checkCartContents().then(() => {
      updateSelectedCount();
    });
    document.addEventListener('cart:update', () => {
      setTimeout(() => checkCartContents(), 500);
    });
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) checkCartContents();
    });
    setInterval(() => checkCartContents(), 5000);
  }
}

function initAllCustomFabricColorOptions() {
  document.querySelectorAll('script[id^="cffco-config-"]').forEach((script) => {
    try {
      const config = JSON.parse(script.textContent);
      if (config && config.blockId != null) {
        initCustomFabricColorOptions(config);
      }
    } catch (_) {}
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllCustomFabricColorOptions);
} else {
  initAllCustomFabricColorOptions();
}
